// ─── Spec C §2 — Kandidaat-pipeline (sollicitatie → conversie) ──────────────
//
// Een Kandidaat is het voortraject vóór een Employee bestaat. Persoonsgegevens,
// vaardigheids-indicaties (checkboxes), recruiter-notities en gesprekken worden
// hier vastgelegd. Bij status `AANGENOMEN` doet `POST /kandidaten/:id/converteer`
// in één transactie:
//   1. Employee aanmaken (status ONBOARDING, persoonsgegevens + contractvelden)
//   2. User aanmaken (hasPortalAccess: true, hasBackofficeAccess: false)
//   3. Onboarding starten (template-selectie, stappen-generatie)
//   4. `employeeId` + `convertedAt` terug-schrijven op Kandidaat
//   5. Audit-trail loggen (KandidaatAudit + OnboardingAudit)
//   6. Welkomstmail triggeren (`ONBOARDING_WELKOM_LINK`)
//
// `email` is niet unique: re-applications van een eerder afgewezen kandidaat
// zijn toegestaan via een nieuw record (spec §16.1).
import { z } from 'zod'
import { idParam } from './id-validators'
import { kandidaatCertificaatVinkjeSchema } from './recruitment-sollicitant'

// ─── Enums (matching Prisma enums as string unions) ─────────────────────────

export const KANDIDAAT_STATUS = [
  'NIEUW',
  'EERSTE_GESPREK',
  'TWEEDE_GESPREK',
  'AANGENOMEN',
  'AFGEWEZEN',
  'ON_HOLD',
  // Batch-7 recruitment (B2) — additieve uitbreiding. Bestaande waarden
  // blijven; de UI groepeert ze (zie KANDIDAAT_STATUS_GROEP).
  'NABELLEN',
  'GESPREK_GEPLAND',
  'GESPREK_GEHAD',
  'BEOORDELING',
  'AANBIEDING_GEDAAN',
  'NIET_GEACCEPTEERD',
  'TALENTPOOL',
  'NIET_BEREIKBAAR',
  // B6 — interim-status tijdens de afwijs-borging.
  'AFWIJZING_BEZIG',
  // FO-517 (spec 217) — gestructureerde beoordelingsworkflow.
  'PRE_SCREENING',
  'RECRUITERBEOORDELING',
  'BEOORDELING_VERVOLG',
  'AANVULLENDE_INFO',
  'TECHNISCHE_VERIFICATIE',
  'REFERENTIECHECK',
  'BESLUIT',
  'INGETROKKEN',
] as const
export type KandidaatStatus = (typeof KANDIDAAT_STATUS)[number]

export const KANDIDAAT_GESPREK_TYPE = [
  // batch-70 (FO-530 A2) — telefonische prescreening als echt gesprek.
  'TELEFONISCH',
  'EERSTE_GESPREK',
  'TWEEDE_GESPREK',
  'ASSESSMENT',
  'PROEFDAG',
] as const
export type KandidaatGesprekType = (typeof KANDIDAAT_GESPREK_TYPE)[number]

export const KANDIDAAT_GESPREK_BEOORDELING = [
  'POSITIEF',
  'NEUTRAAL',
  'NEGATIEF',
] as const
export type KandidaatGesprekBeoordeling =
  (typeof KANDIDAAT_GESPREK_BEOORDELING)[number]

// ─── Labels (NL-talig voor UI) ──────────────────────────────────────────────

export const KANDIDAAT_STATUS_LABELS: Record<KandidaatStatus, string> = {
  NIEUW:             'Nieuw',
  EERSTE_GESPREK:    'Eerste gesprek',
  TWEEDE_GESPREK:    'Tweede gesprek',
  AANGENOMEN:        'Aangenomen',
  AFGEWEZEN:         'Afgewezen',
  ON_HOLD:           'On hold',
  NABELLEN:          'Nabellen',
  GESPREK_GEPLAND:   'Gesprek gepland',
  GESPREK_GEHAD:     'Gesprek gehad',
  BEOORDELING:       'Beoordeling',
  AANBIEDING_GEDAAN: 'Aanbieding gedaan',
  NIET_GEACCEPTEERD: 'Niet geaccepteerd',
  TALENTPOOL:        'Talentpool',
  NIET_BEREIKBAAR:   'Niet bereikbaar',
  AFWIJZING_BEZIG:   'Afwijzing bezig',
  PRE_SCREENING:          'Pre-screening',
  RECRUITERBEOORDELING:   'Recruiterbeoordeling',
  BEOORDELING_VERVOLG:    'Second review',
  AANVULLENDE_INFO:       'Aanvullende info',
  TECHNISCHE_VERIFICATIE: 'Technische verificatie',
  REFERENTIECHECK:        'Referentiecheck',
  BESLUIT:                'Besluit',
  INGETROKKEN:            'Ingetrokken',
}

/**
 * Spec §2.4 + batch-7 B2/B3 — toegestane status-overgangen. Eén bron van
 * waarheid voor API én UI (voorheen drie gespiegelde kopieën): de UI toont
 * alleen overgangen die de server accepteert, de API valideert dezelfde set.
 *
 * Let op (B6): `AFGEWEZEN` is als overgang bereikbaar, maar de API legt
 * daarbovenop de afwijs-borging op (reden + afwijsmail + telefoonactie).
 */
const KANDIDAAT_PARKEER_TRANSITIES: readonly KandidaatStatus[] = [
  'AFWIJZING_BEZIG',
  'AFGEWEZEN',
  'ON_HOLD',
  'TALENTPOOL',
  // FO-517 §5.4 — kandidaat trekt zich terug (met reden).
  'INGETROKKEN',
]

// FO-517 §G12 — de lussen binnen de second review (info, verificatie,
// referentie) keren terug naar de review, een herbeoordeling of het besluit.
const KANDIDAAT_REVIEW_LUS_TERUG: readonly KandidaatStatus[] = [
  'BEOORDELING_VERVOLG',
  'RECRUITERBEOORDELING',
  'BESLUIT',
]

export const KANDIDAAT_STATUS_TRANSITIES: Record<KandidaatStatus, readonly KandidaatStatus[]> = {
  NIEUW:             ['NABELLEN', 'PRE_SCREENING', 'GESPREK_GEPLAND', 'EERSTE_GESPREK', 'NIET_BEREIKBAAR', ...KANDIDAAT_PARKEER_TRANSITIES],
  NABELLEN:          ['PRE_SCREENING', 'GESPREK_GEPLAND', 'EERSTE_GESPREK', 'NIET_BEREIKBAAR', ...KANDIDAAT_PARKEER_TRANSITIES],
  NIET_BEREIKBAAR:   ['NABELLEN', ...KANDIDAAT_PARKEER_TRANSITIES],
  PRE_SCREENING:     ['GESPREK_GEPLAND', 'NABELLEN', ...KANDIDAAT_PARKEER_TRANSITIES],
  // FO-517: "Gesprek gevoerd" vanuit de geplande stand gaat direct naar de recruiterbeoordeling
  // (anders bleef de kandidaat stil op GESPREK_GEPLAND staan en liepen de 48-uurscron en de
  // second review niet mee).
  GESPREK_GEPLAND:   ['GESPREK_GEHAD', 'EERSTE_GESPREK', 'TWEEDE_GESPREK', 'RECRUITERBEOORDELING', 'NABELLEN', ...KANDIDAAT_PARKEER_TRANSITIES],
  EERSTE_GESPREK:    ['TWEEDE_GESPREK', 'GESPREK_GEPLAND', 'GESPREK_GEHAD', 'BEOORDELING', 'RECRUITERBEOORDELING', ...KANDIDAAT_PARKEER_TRANSITIES],
  TWEEDE_GESPREK:    ['AANGENOMEN', 'BEOORDELING', 'RECRUITERBEOORDELING', 'BEOORDELING_VERVOLG', 'BESLUIT', 'GESPREK_GEPLAND', 'AANBIEDING_GEDAAN', ...KANDIDAAT_PARKEER_TRANSITIES],
  GESPREK_GEHAD:     ['RECRUITERBEOORDELING', 'BEOORDELING', 'GESPREK_GEPLAND', 'AANBIEDING_GEDAAN', ...KANDIDAAT_PARKEER_TRANSITIES],
  BEOORDELING:       ['AANBIEDING_GEDAAN', 'GESPREK_GEPLAND', 'AANGENOMEN', 'RECRUITERBEOORDELING', ...KANDIDAAT_PARKEER_TRANSITIES],
  RECRUITERBEOORDELING:   ['BEOORDELING_VERVOLG', 'GESPREK_GEPLAND', 'AANVULLENDE_INFO', 'TECHNISCHE_VERIFICATIE', ...KANDIDAAT_PARKEER_TRANSITIES],
  BEOORDELING_VERVOLG:    ['AANVULLENDE_INFO', 'TECHNISCHE_VERIFICATIE', 'REFERENTIECHECK', 'TWEEDE_GESPREK', 'GESPREK_GEPLAND', 'RECRUITERBEOORDELING', 'BESLUIT', ...KANDIDAAT_PARKEER_TRANSITIES],
  AANVULLENDE_INFO:       [...KANDIDAAT_REVIEW_LUS_TERUG, ...KANDIDAAT_PARKEER_TRANSITIES],
  TECHNISCHE_VERIFICATIE: [...KANDIDAAT_REVIEW_LUS_TERUG, ...KANDIDAAT_PARKEER_TRANSITIES],
  REFERENTIECHECK:        [...KANDIDAAT_REVIEW_LUS_TERUG, ...KANDIDAAT_PARKEER_TRANSITIES],
  BESLUIT:           ['AANBIEDING_GEDAAN', 'TWEEDE_GESPREK', 'GESPREK_GEPLAND', 'RECRUITERBEOORDELING', 'AANVULLENDE_INFO', ...KANDIDAAT_PARKEER_TRANSITIES],
  AANBIEDING_GEDAAN: ['AANGENOMEN', 'NIET_GEACCEPTEERD', ...KANDIDAAT_PARKEER_TRANSITIES],
  NIET_GEACCEPTEERD: ['AANBIEDING_GEDAAN', ...KANDIDAAT_PARKEER_TRANSITIES],
  TALENTPOOL:        ['NABELLEN', 'PRE_SCREENING', 'GESPREK_GEPLAND', 'AANBIEDING_GEDAAN', 'AFWIJZING_BEZIG', 'AFGEWEZEN', 'ON_HOLD', 'INGETROKKEN'],
  AANGENOMEN:        ['AFGEWEZEN', 'ON_HOLD'],
  AFGEWEZEN:         ['ON_HOLD', 'TALENTPOOL'],
  AFWIJZING_BEZIG:   ['AFGEWEZEN'],
  INGETROKKEN:       ['ON_HOLD', 'TALENTPOOL'],
  ON_HOLD:           ['NIEUW', 'NABELLEN', 'PRE_SCREENING', 'EERSTE_GESPREK', 'TWEEDE_GESPREK', 'GESPREK_GEPLAND', 'BEOORDELING', 'RECRUITERBEOORDELING', 'BEOORDELING_VERVOLG', 'BESLUIT', 'AANBIEDING_GEDAAN', 'TALENTPOOL', 'AFWIJZING_BEZIG', 'AFGEWEZEN', 'AANGENOMEN', 'INGETROKKEN'],
}

export function isKandidaatStatusTransitionAllowed(
  van: KandidaatStatus,
  naar: KandidaatStatus,
): boolean {
  if (van === naar) return false // no-op is geen transitie
  return KANDIDAAT_STATUS_TRANSITIES[van].includes(naar)
}

/**
 * UI-groepering van statussen (B2): de uitgebreide set blijft in lijsten en
 * filters hanteerbaar door ze logisch te groeperen. Volgorde = workflow.
 */
export const KANDIDAAT_STATUS_GROEPEN: ReadonlyArray<{
  key: string
  label: string
  statussen: ReadonlyArray<KandidaatStatus>
}> = [
  { key: 'instroom',   label: 'Instroom',   statussen: ['NIEUW', 'NABELLEN', 'NIET_BEREIKBAAR', 'PRE_SCREENING'] },
  { key: 'gesprekken', label: 'Gesprekken', statussen: ['GESPREK_GEPLAND', 'EERSTE_GESPREK', 'TWEEDE_GESPREK', 'GESPREK_GEHAD', 'BEOORDELING'] },
  // FO-517 — beoordelingsworkflow (recruiter, second review, besluit).
  { key: 'beoordeling', label: 'Beoordeling', statussen: ['RECRUITERBEOORDELING', 'BEOORDELING_VERVOLG', 'AANVULLENDE_INFO', 'TECHNISCHE_VERIFICATIE', 'REFERENTIECHECK', 'BESLUIT'] },
  { key: 'aanbieding', label: 'Aanbieding', statussen: ['AANBIEDING_GEDAAN', 'NIET_GEACCEPTEERD'] },
  { key: 'aangenomen', label: 'Aangenomen', statussen: ['AANGENOMEN'] },
  { key: 'parkeer',    label: 'Talentpool & on hold', statussen: ['TALENTPOOL', 'ON_HOLD'] },
  { key: 'afgewezen',  label: 'Afgewezen',  statussen: ['AFWIJZING_BEZIG', 'AFGEWEZEN', 'INGETROKKEN'] },
]

export const KANDIDAAT_GESPREK_TYPE_LABELS: Record<KandidaatGesprekType, string> = {
  TELEFONISCH:    'Telefonisch gesprek',
  EERSTE_GESPREK: 'Eerste gesprek',
  TWEEDE_GESPREK: 'Tweede gesprek',
  ASSESSMENT:     'Assessment',
  PROEFDAG:       'Proefdag',
}

export const KANDIDAAT_GESPREK_BEOORDELING_LABELS: Record<
  KandidaatGesprekBeoordeling,
  string
> = {
  POSITIEF: 'Positief',
  NEUTRAAL: 'Neutraal',
  NEGATIEF: 'Negatief',
}

// Status badge variant (consistent met andere modules — StatusBadge-component).
export const KANDIDAAT_STATUS_BADGE_VARIANT: Record<
  KandidaatStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  NIEUW:             'info',      // blauw — net binnen
  EERSTE_GESPREK:    'warning',   // oranje — in pipeline
  TWEEDE_GESPREK:    'warning',   // oranje — in pipeline (verder)
  AANGENOMEN:        'success',   // groen — geconverteerd of klaar voor conversie
  AFGEWEZEN:         'danger',    // rood — afgewezen
  ON_HOLD:           'secondary', // grijs — geparkeerd
  NABELLEN:          'info',      // blauw — instroom-actie
  GESPREK_GEPLAND:   'warning',   // oranje — pipeline
  GESPREK_GEHAD:     'warning',   // oranje — pipeline
  BEOORDELING:       'warning',   // oranje — pipeline
  AANBIEDING_GEDAAN: 'warning',   // oranje — wacht op kandidaat
  NIET_GEACCEPTEERD: 'danger',    // rood — aanbod afgewezen door kandidaat
  TALENTPOOL:        'secondary', // grijs — geparkeerd voor later
  NIET_BEREIKBAAR:   'secondary', // grijs — instroom vastgelopen
  AFWIJZING_BEZIG:   'danger',    // rood — afwijzing loopt nog
  PRE_SCREENING:          'info',
  RECRUITERBEOORDELING:   'warning',
  BEOORDELING_VERVOLG:    'warning',
  AANVULLENDE_INFO:       'warning',
  TECHNISCHE_VERIFICATIE: 'warning',
  REFERENTIECHECK:        'warning',
  BESLUIT:                'warning',
  INGETROKKEN:            'secondary',
}

// ─── DTOs voor API responses ────────────────────────────────────────────────

/**
 * Basis-DTO voor Kandidaat — gebruikt als "shape" door zowel List- als Detail-
 * DTO's. Spec §13.1 noemt geen losse `KandidaatDTO`, maar voor type-veiligheid
 * binnen de codebase is het handig één canonical type te hebben.
 */
export interface KandidaatDTO {
  id: string
  entityId: string | null
  entityName: string | null
  voornaam: string
  achternaam: string
  geboortedatum: string | null
  woonplaats: string | null
  telefoon: string
  email: string
  functie: string
  status: KandidaatStatus
  bron: string | null
  // Vaardigheden-indicatie (checkboxes uit sollicitatieformulier)
  mechanisch: boolean
  elektrotechniek: boolean
  plcSoftware: boolean
  tigMigLassen: boolean
  storingstechniek: boolean
  ploegdienstErvaring: boolean
  rijbewijsCategorie: string | null
  certificaten: string | null
  // Meta
  opmerkingenRecruiter: string | null
  afwijsReden: string | null
  // ── Batch-7 B6 — afwijs-borging ──────────────────────────────────────────
  afwijsRedenCode: KandidaatAfwijsReden | null
  afwijsMailVerzondenOp: string | null
  afwijsTelefoonActieOp: string | null
  // ── Batch-7 B2 — profiel-uitbreiding ─────────────────────────────────────
  beschikbaarVanaf: string | null
  huidigeFunctie: string | null
  niveau: KandidaatNiveau | null
  salarisIndicatie: number | null
  talen: string | null
  sectorErvaring: string[]
  straat: string | null
  huisnummer: string | null
  postcode: string | null
  recruiterId: string | null
  recruiterNaam: string | null
  volgendeActieOp: string | null
  volgendeActieOmschrijving: string | null
  laatsteContactOp: string | null
  heeftCvTekst: boolean
  ingevoerdDoor: string
  ingevoerdDoorNaam: string | null
  employeeId: string | null
  convertedAt: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Lijst-row voor de kandidaten-kanban/lijst-view. `dagenInStatus` is computed
 * uit de meest recente STATUS_GEWIJZIGD-audit (of `createdAt` als fallback).
 */
export interface KandidaatListItemDTO {
  id: string
  voornaam: string
  achternaam: string
  functie: string
  status: KandidaatStatus
  bron: string | null
  email: string
  telefoon: string
  dagenInStatus: number
  createdAt: string
}

export interface KandidaatNotitieDTO {
  id: string
  userId: string
  userNaam: string | null
  inhoud: string
  // Batch-7 B2 — dagtekening-type + vervolg-actie + optionele bijlage.
  type: KandidaatNotitieType
  volgendeActie: string | null
  documentId: string | null
  bewerktOp: string | null
  createdAt: string
}

export interface KandidaatGesprekDTO {
  id: string
  type: KandidaatGesprekType
  meetingLink?: string | null
  datum: string
  locatie: string | null
  gespreksleider: string
  gespreksleiderNaam: string | null
  samenvatting: string | null
  beoordeling: KandidaatGesprekBeoordeling | null
  createdAt: string
  updatedAt: string
}

export interface KandidaatAuditDTO {
  id: string
  actie: string
  actor: string
  actorNaam: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

/**
 * Detail-DTO voor `/kandidaten/[id]` — inclusief audits, notities en gesprekken.
 * Velden komen 1:1 uit `KandidaatDTO`, plus de drie nested lists.
 */
export interface KandidaatDetailDTO extends KandidaatDTO {
  audits: KandidaatAuditDTO[]
  notities: KandidaatNotitieDTO[]
  gesprekken: KandidaatGesprekDTO[]
  // FO-101 — gevuld als de kandidaat uit de recruitment-mailbox-ingest kwam
  // ("Binnengekomen via recruitment@ op <datum>"). Null bij handmatige invoer.
  mailHerkomst: { ontvangenOp: string; mailbox: string } | null
}

// ─── Zod-schemas voor API-validatie ─────────────────────────────────────────

/**
 * Zod-validator voor ISO-datum `YYYY-MM-DD` (consistent met andere modules,
 * bv. `verlof.ts`, `inkoop.ts`). Voor JSON-roundtrip houden we datums als
 * string in de API; conversie naar `Date` gebeurt in de route-handler.
 */
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datumformaat (yyyy-MM-dd)')

/** Spec §2.2 — nieuwe kandidaat invoeren (recruiter-formulier). */
export const KANDIDAAT_NIVEAU = ['JUNIOR', 'MEDIOR', 'SENIOR'] as const
export type KandidaatNiveau = (typeof KANDIDAAT_NIVEAU)[number]

export const KANDIDAAT_NIVEAU_LABELS: Record<KandidaatNiveau, string> = {
  JUNIOR: 'Junior',
  MEDIOR: 'Medior',
  SENIOR: 'Senior',
}

export const KANDIDAAT_AFWIJS_REDEN = [
  'ONVOLDOENDE_ERVARING',
  'NIET_PASSEND',
  'SALARISWENS',
  'AFSTAND',
  'CULTUUR',
  'GEEN_REACTIE',
  'ANDERE_KANDIDAAT',
  'NIET_BESCHIKBAAR',
  'CERTIFICATEN',
  // FO-517 §I21 — redenen die uit de gestructureerde beoordeling volgen.
  'TECHNISCH_NIVEAU',
  'VERWACHTINGEN',
  'OVERIG',
] as const
export type KandidaatAfwijsReden = (typeof KANDIDAAT_AFWIJS_REDEN)[number]

export const KANDIDAAT_AFWIJS_REDEN_LABELS: Record<KandidaatAfwijsReden, string> = {
  ONVOLDOENDE_ERVARING: 'Onvoldoende ervaring',
  NIET_PASSEND: 'Niet passend bij de functie',
  SALARISWENS: 'Salariswens te hoog',
  AFSTAND: 'Reisafstand',
  CULTUUR: 'Past niet bij de cultuur',
  GEEN_REACTIE: 'Geen reactie van kandidaat',
  ANDERE_KANDIDAAT: 'Andere kandidaat gekozen',
  NIET_BESCHIKBAAR: 'Niet (meer) beschikbaar',
  CERTIFICATEN: 'Certificaten ontbreken',
  TECHNISCH_NIVEAU: 'Technisch niveau onvoldoende',
  VERWACHTINGEN: 'Verwachtingen sluiten niet aan',
  OVERIG: 'Overig',
}

/**
 * Batch-7 B6 — afwijs-borging: de status wordt pas definitief AFGEWEZEN
 * wanneer reden + afwijsmail + telefoonnotitie-of-beltaak alle drie
 * vastliggen (server-side afgedwongen). Tot die tijd staat de kandidaat op
 * de interim-status AFWIJZING_BEZIG met een reminder.
 */
export const afwijzenStartSchema = z.object({
  redenCode: z.enum(KANDIDAAT_AFWIJS_REDEN),
  toelichting: z.string().trim().max(2000).nullable().optional(),
  /**
   * FO-517 §I21 — interne onderbouwing (verplicht zodra er een afgeronde
   * beoordeling is, herleidbaar naar die beoordeling). Strikt intern: komt
   * NOOIT in de kandidaatmail (aparte velden, drift-test).
   */
  interneOnderbouwing: z.string().trim().max(8000).nullable().optional(),
})
export type AfwijzenStartInput = z.infer<typeof afwijzenStartSchema>

export const afwijzenMailSchema = z.object({
  onderwerp: z.string().trim().min(1).max(200).optional(),
  body: z.string().trim().min(1).max(10_000).optional(),
})
export type AfwijzenMailInput = z.infer<typeof afwijzenMailSchema>

export const afwijzenTelefoonactieSchema = z
  .object({
    soort: z.enum(['NOTITIE', 'BELTAAK']),
    notitie: z.string().trim().max(5000).optional(),
    belDatum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.soort === 'NOTITIE' && (!val.notitie || val.notitie.trim().length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['notitie'],
        message: 'Vul de telefoonnotitie in.',
      })
    }
    if (val.soort === 'BELTAAK' && !val.belDatum) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['belDatum'],
        message: 'Kies een datum voor de beltaak.',
      })
    }
  })
export type AfwijzenTelefoonactieInput = z.infer<typeof afwijzenTelefoonactieSchema>

/** Voortgang van de afwijs-borging (voor de UI-stappenweergave). */
export interface AfwijsBorgingDTO {
  redenVastgelegd: boolean
  mailVerzonden: boolean
  telefoonactieVastgelegd: boolean
  compleet: boolean
}

export const KANDIDAAT_NOTITIE_TYPE = [
  'ALGEMEEN',
  'TELEFONISCH',
  'WHATSAPP',
  'EMAIL',
  'GESPREK',
  'SALARIS',
  'BESCHIKBAARHEID',
  'AFWIJZING',
  'AANBOD',
  'INTERNE_BEOORDELING',
] as const
export type KandidaatNotitieType = (typeof KANDIDAAT_NOTITIE_TYPE)[number]

export const KANDIDAAT_NOTITIE_TYPE_LABELS: Record<KandidaatNotitieType, string> = {
  ALGEMEEN: 'Algemeen',
  TELEFONISCH: 'Telefonisch',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
  GESPREK: 'Gesprek',
  SALARIS: 'Salaris',
  BESCHIKBAARHEID: 'Beschikbaarheid',
  AFWIJZING: 'Afwijzing',
  AANBOD: 'Aanbod',
  INTERNE_BEOORDELING: 'Interne beoordeling',
}

export const createKandidaatSchema = z.object({
  entityId:            idParam('entityId').optional(),
  voornaam:            z.string().trim().min(1).max(100),
  achternaam:          z.string().trim().min(1).max(100),
  geboortedatum:       isoDate.optional(),
  woonplaats:          z.string().trim().max(150).optional(),
  telefoon:            z.string().trim().min(1).max(50),
  email:               z.string().trim().email().max(255),
  functie:             z.string().trim().min(1).max(150),
  // Vaardigheden-checkboxen — allemaal optional met default false in service.
  mechanisch:          z.boolean().optional(),
  elektrotechniek:     z.boolean().optional(),
  plcSoftware:         z.boolean().optional(),
  tigMigLassen:        z.boolean().optional(),
  storingstechniek:    z.boolean().optional(),
  ploegdienstErvaring: z.boolean().optional(),
  rijbewijsCategorie:  z.string().trim().max(20).optional(),
  certificaten:        z.string().trim().max(500).optional(),
  // Meta
  // batch-70 (FO-530 §A): null = bron wissen (de read-DTO geeft hem ook als null terug).
  bron:                z.string().trim().max(100).nullable().optional(),
  opmerkingenRecruiter: z.string().trim().max(5000).optional(),
  // ── Batch-7 B2 — profiel-uitbreiding ─────────────────────────────────────
  beschikbaarVanaf:    isoDate.nullable().optional(),
  huidigeFunctie:      z.string().trim().max(150).nullable().optional(),
  niveau:              z.enum(KANDIDAAT_NIVEAU).nullable().optional(),
  salarisIndicatie:    z.number().min(0).max(99_999_999).nullable().optional(),
  talen:               z.string().trim().max(300).nullable().optional(),
  sectorErvaring:      z.array(z.string().trim().min(1).max(80)).max(30).optional(),
  straat:              z.string().trim().max(150).nullable().optional(),
  huisnummer:          z.string().trim().max(20).nullable().optional(),
  postcode:            z.string().trim().max(20).nullable().optional(),
  recruiterId:         idParam('recruiterId').nullable().optional(),
  volgendeActieOp:     isoDate.nullable().optional(),
  volgendeActieOmschrijving: z.string().trim().max(300).nullable().optional(),
})
export type CreateKandidaatInput = z.infer<typeof createKandidaatSchema>

/**
 * batch-70 (FO-530 §C4) — `POST /kandidaten` neemt de certificaat-vinkjes van
 * het nieuw-formulier mee (zelfde transactie, rijen in KandidaatOpleiding). Los
 * van `createKandidaatSchema` gehouden zodat de PATCH (`.partial()`) ze niet stil
 * negeert: wijzigen gaat via `PUT /kandidaten/:id/certificaten`.
 */
export const nieuweKandidaatSchema = createKandidaatSchema.extend({
  certificaatVinkjes: z.array(kandidaatCertificaatVinkjeSchema).max(100).optional(),
})
export type NieuweKandidaatInput = z.infer<typeof nieuweKandidaatSchema>

/**
 * Spec §2.2 — partial update voor `PATCH /kandidaten/:id`. Status-wijziging
 * gaat via een aparte route (`POST /kandidaten/:id/status`) — daarom is
 * `status` hier bewust niet opgenomen.
 */
export const updateKandidaatSchema = createKandidaatSchema.partial()
export type UpdateKandidaatInput = z.infer<typeof updateKandidaatSchema>

/**
 * Spec §2.2 — statuswijziging via `POST /kandidaten/:id/status`. De route-
 * handler valideert dat de overgang toegestaan is (bv. NIEUW → EERSTE_GESPREK,
 * niet NIEUW → AANGENOMEN). `afwijsReden` is verplicht bij overgang naar
 * `AFGEWEZEN` — deze cross-field-check zit in de route-handler (Zod kan ook
 * `.refine()` maar het laten we generiek hier).
 */
export const kandidaatStatusWijzigingSchema = z.object({
  status:      z.enum(KANDIDAAT_STATUS),
  afwijsReden: z.string().trim().max(2000).optional(),
  toelichting: z.string().trim().max(2000).optional(),
})
export type KandidaatStatusWijzigingInput = z.infer<
  typeof kandidaatStatusWijzigingSchema
>

/** Spec §2.2 — notitie toevoegen via `POST /kandidaten/:id/notitie`. */
export const kandidaatNotitieSchema = z.object({
  inhoud: z.string().trim().min(1).max(5000),
  // Batch-7 B2 — dagtekening-type + optionele vervolg-actie + bijlage.
  type: z.enum(KANDIDAAT_NOTITIE_TYPE).optional(),
  volgendeActie: z.string().trim().max(300).nullable().optional(),
  documentId: z.string().min(1).nullable().optional(),
})
export type KandidaatNotitieInput = z.infer<typeof kandidaatNotitieSchema>

/**
 * Batch-7 B2 — notities zijn append-only: bewerken schrijft de oude tekst
 * naar KandidaatAudit, verwijderen is uitsluitend soft (+ audit).
 */
export const kandidaatNotitieUpdateSchema = z.object({
  inhoud: z.string().trim().min(1).max(5000).optional(),
  type: z.enum(KANDIDAAT_NOTITIE_TYPE).optional(),
  volgendeActie: z.string().trim().max(300).nullable().optional(),
})
export type KandidaatNotitieUpdateInput = z.infer<typeof kandidaatNotitieUpdateSchema>

/** Spec §2.2 — gesprek plannen/registreren via `POST /kandidaten/:id/gesprek`. */
export const kandidaatGesprekSchema = z.object({
  type:           z.enum(KANDIDAAT_GESPREK_TYPE),
  datum:          isoDate,
  locatie:        z.string().trim().max(200).optional(),
  gespreksleider: idParam('gespreksleider'),
  samenvatting:   z.string().trim().max(5000).optional(),
  beoordeling:    z.enum(KANDIDAAT_GESPREK_BEOORDELING).optional(),
  // Batch-7 B7 — online-meeting-link (Teams/Meet).
  meetingLink:    z.string().trim().url().max(500).nullable().optional(),
})
export type KandidaatGesprekInput = z.infer<typeof kandidaatGesprekSchema>

/**
 * Spec §2.3 — conversie-input voor `POST /kandidaten/:id/converteer`.
 *
 * **Belangrijk (kruisverband ❹c uit de meesterprompt):** deze schema definieert
 * uitsluitend de *contract-input-velden* die door
 * `createEmployeeWithInitialMutations` (PR A6) gebruikt worden. NIET direct in
 * `tx.employee.create(...)`. De PR-C2 conversie-route mapt deze input naar
 * de gedeelde service-call; persoonsgegevens (voornaam/achternaam/email/etc.)
 * worden uit het Kandidaat-record gehaald en hoeven dus niet opnieuw door de
 * client meegestuurd te worden.
 *
 * Optionele `onboardingTemplateId` + `onboardingDeadline` sturen direct de
 * Onboarding-aanmaak in dezelfde transactie.
 */
export const converteerSchema = z
  .object({
    functie:              z.string().trim().min(1).max(150),
    // Afdeling is optioneel: Employee.departmentId is nullable en de directe
    // POST /employees-flow vereist 'm ook niet (HR vult 'm later in het
    // dossier). Wél valideren wanneer opgegeven (geldig cuid/uuid).
    departmentId:         idParam('departmentId').optional(),
    entityId:             idParam('entityId'),
    contractType:         z.enum(['BEPAALDE_TIJD', 'ONBEPAALDE_TIJD']),
    startDatum:           isoDate,
    eindDatum:            isoDate.optional(),
    contractHoursPerWeek: z.number().min(0).max(60).default(38),
    grossMonthlySalary:   z.number().min(0).optional(),
    caoSchaal:            z.string().trim().max(50).optional(),
    caoTrede:             z.number().int().optional(),
    // Optioneel: template-selectie + deadline voor de aan te maken Onboarding.
    onboardingTemplateId: idParam('onboardingTemplateId').optional(),
    onboardingDeadline:   isoDate.optional(),
  })
  // Bij een contract voor bepaalde tijd is een einddatum verplicht, en die
  // datum mag niet vóór de startdatum liggen. Datum-vergelijking op de
  // ISO-string (yyyy-MM-dd) is veilig: lexicografische orde == chronologische
  // orde, dus geen Date/timezone-conversie nodig.
  .refine(
    (v) => v.contractType !== 'BEPAALDE_TIJD' || (v.eindDatum != null && v.eindDatum !== ''),
    { message: 'Bij een contract voor bepaalde tijd is een einddatum verplicht', path: ['eindDatum'] },
  )
  .refine(
    (v) => v.eindDatum == null || v.eindDatum >= v.startDatum,
    { message: 'Einddatum mag niet vóór de startdatum liggen', path: ['eindDatum'] },
  )
export type ConverteerInput = z.infer<typeof converteerSchema>

// batch-70 (FO-530) — bronlijst en certificaat-vinkmodel (eigen module, via deze export).
export * from './recruitment-sollicitant'
